import { useState, useEffect, useRef } from 'react';
import { Menu, X, Search, ShoppingCart, ChevronDown, User } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const timeoutRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Gestion avec délai pour éviter fermeture accidentelle
  const handleDropdownEnter = (label) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setActiveDropdown(label);
  };

  const handleDropdownLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setActiveDropdown(null);
    }, 150); // Délai de 150ms avant fermeture
  };

  const handleSubmenuEnter = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  return (
    <nav
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled
          ? 'bg-[#1C1917]/98 backdrop-blur-md py-3 shadow-lg'
          : 'bg-[#1C1917]/90 backdrop-blur-sm py-4'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8 flex items-center justify-between">
        {/* Logo */}
        <a href="https://gillesdelieuze.com" className="flex items-center" data-testid="logo-link">
          <img
            src="https://gillesdelieuze.com/wp-content/uploads/2022/12/Logo_Final_3bd-.png"
            alt="Gilles Delieuze"
            className="h-10 md:h-12 w-auto"
          />
        </a>

        {/* Desktop Menu */}
        <div className="hidden lg:flex items-center gap-1">
          
          {/* FORMATIONS */}
          <div
            className="relative"
            onMouseEnter={() => handleDropdownEnter('formations')}
            onMouseLeave={handleDropdownLeave}
          >
            <span className="flex items-center gap-1 text-[#FAFAF9] text-sm uppercase tracking-wider font-medium px-4 py-2 hover:text-[#D4AF37] transition-colors duration-300 cursor-pointer">
              Formations
              <ChevronDown size={14} className="opacity-60" />
            </span>
            
            {activeDropdown === 'formations' && (
              <div 
                className="absolute top-full left-0 pt-2"
                onMouseEnter={handleSubmenuEnter}
                onMouseLeave={handleDropdownLeave}
              >
                <div className="bg-[#1C1917] border border-[#D4AF37]/20 rounded-lg shadow-xl min-w-[280px] overflow-visible">
                  <a href="https://gillesdelieuze.com/lecole-des-maitres-dauphins/" className="block px-5 py-3 text-[#A8A29E] text-sm hover:text-[#FAFAF9] hover:bg-[#D4AF37]/10 transition-colors">
                    École Dauphins
                  </a>
                  <a href="https://gillesdelieuze.com/accordeur-quantique-2024/" className="block px-5 py-3 text-[#A8A29E] text-sm hover:text-[#FAFAF9] hover:bg-[#D4AF37]/10 transition-colors">
                    Accordeur Quantique
                  </a>
                  
                  {/* Toutes les formations - sous-menu */}
                  <div className="relative group/toutes">
                    <div className="flex items-center justify-between px-5 py-3 text-[#A8A29E] text-sm hover:text-[#FAFAF9] hover:bg-[#D4AF37]/10 transition-colors cursor-pointer">
                      Toutes les formations
                      <ChevronDown size={12} className="-rotate-90" />
                    </div>
                    <div className="absolute left-full top-0 pl-2 hidden group-hover/toutes:block">
                      <div className="bg-[#1C1917] border border-[#D4AF37]/20 rounded-lg shadow-xl min-w-[220px]">
                        <a href="https://gillesdelieuze.com/quantic-revolution/" className="block px-5 py-3 text-[#A8A29E] text-sm hover:text-[#FAFAF9] hover:bg-[#D4AF37]/10 transition-colors">
                          Quantic Revolution
                        </a>
                        <a href="https://gillesdelieuze.com/galactique-pdv/" className="block px-5 py-3 text-[#A8A29E] text-sm hover:text-[#FAFAF9] hover:bg-[#D4AF37]/10 transition-colors">
                          Initiation Langage Galactique
                        </a>
                        <a href="https://gillesdelieuze.com/processus-morpheus/" className="block px-5 py-3 text-[#A8A29E] text-sm hover:text-[#FAFAF9] hover:bg-[#D4AF37]/10 transition-colors">
                          Morpheus
                        </a>
                      </div>
                    </div>
                  </div>
                  
                  {/* Programmes - sous-menu */}
                  <div className="relative group/prog">
                    <div className="flex items-center justify-between px-5 py-3 text-[#A8A29E] text-sm hover:text-[#FAFAF9] hover:bg-[#D4AF37]/10 transition-colors cursor-pointer">
                      Programmes
                      <ChevronDown size={12} className="-rotate-90" />
                    </div>
                    <div className="absolute left-full top-0 pl-2 hidden group-hover/prog:block">
                      <div className="bg-[#1C1917] border border-[#D4AF37]/20 rounded-lg shadow-xl min-w-[220px]">
                        <a href="https://gillesdelieuze.com/quantum-love/" className="block px-5 py-3 text-[#A8A29E] text-sm hover:text-[#FAFAF9] hover:bg-[#D4AF37]/10 transition-colors">
                          Quantum Love
                        </a>
                        <a href="https://gillesdelieuze.com/intuitive-clarity/" className="block px-5 py-3 text-[#A8A29E] text-sm hover:text-[#FAFAF9] hover:bg-[#D4AF37]/10 transition-colors">
                          Intuitive Clarity
                        </a>
                        <a href="https://gillesdelieuze.com/dolphin-bliss/" className="block px-5 py-3 text-[#A8A29E] text-sm hover:text-[#FAFAF9] hover:bg-[#D4AF37]/10 transition-colors">
                          Dolphin Bliss
                        </a>
                        <a href="https://gillesdelieuze.com/infinity-serenity/" className="block px-5 py-3 text-[#A8A29E] text-sm hover:text-[#FAFAF9] hover:bg-[#D4AF37]/10 transition-colors">
                          Infinity Serenity
                        </a>
                        <a href="https://gillesdelieuze.com/quantum-harmonics/" className="block px-5 py-3 text-[#A8A29E] text-sm hover:text-[#FAFAF9] hover:bg-[#D4AF37]/10 transition-colors">
                          Quantum Harmonics
                        </a>
                      </div>
                    </div>
                  </div>
                  
                  <a href="https://gillesdelieuze.com/dolphin-bootcamp/" className="block px-5 py-3 text-[#A8A29E] text-sm hover:text-[#FAFAF9] hover:bg-[#D4AF37]/10 transition-colors">
                    Dolphin Bootcamp
                  </a>
                  <a href="https://gillesdelieuze.com/programme-joie-dauphin/" className="block px-5 py-3 text-[#A8A29E] text-sm hover:text-[#FAFAF9] hover:bg-[#D4AF37]/10 transition-colors">
                    Renaître à la Joie
                  </a>
                </div>
              </div>
            )}
          </div>

          {/* VOYAGES */}
          <div
            className="relative"
            onMouseEnter={() => handleDropdownEnter('voyages')}
            onMouseLeave={handleDropdownLeave}
          >
            <span className="flex items-center gap-1 text-[#FAFAF9] text-sm uppercase tracking-wider font-medium px-4 py-2 hover:text-[#D4AF37] transition-colors duration-300 cursor-pointer">
              Voyages
              <ChevronDown size={14} className="opacity-60" />
            </span>
            
            {activeDropdown === 'voyages' && (
              <div 
                className="absolute top-full left-0 pt-2"
                onMouseEnter={handleSubmenuEnter}
                onMouseLeave={handleDropdownLeave}
              >
                <div className="bg-[#1C1917] border border-[#D4AF37]/20 rounded-lg shadow-xl min-w-[200px]">
                  <a href="https://gillesdelieuze.com/voyages-gd/" className="block px-5 py-3 text-[#A8A29E] text-sm hover:text-[#FAFAF9] hover:bg-[#D4AF37]/10 transition-colors">
                    Tous les voyages
                  </a>
                  <a href="https://gillesdelieuze.com/voyage-dauphin/" className="block px-5 py-3 text-[#A8A29E] text-sm hover:text-[#FAFAF9] hover:bg-[#D4AF37]/10 transition-colors">
                    Dauphins Mer Rouge
                  </a>
                  <a href="https://gillesdelieuze.com/madagascar/" className="block px-5 py-3 text-[#A8A29E] text-sm hover:text-[#FAFAF9] hover:bg-[#D4AF37]/10 transition-colors">
                    Madagascar
                  </a>
                </div>
              </div>
            )}
          </div>

          {/* MENTORAT */}
          <div
            className="relative"
            onMouseEnter={() => handleDropdownEnter('mentorat')}
            onMouseLeave={handleDropdownLeave}
          >
            <span className="flex items-center gap-1 text-[#FAFAF9] text-sm uppercase tracking-wider font-medium px-4 py-2 hover:text-[#D4AF37] transition-colors duration-300 cursor-pointer">
              Mentorat
              <ChevronDown size={14} className="opacity-60" />
            </span>
            
            {activeDropdown === 'mentorat' && (
              <div 
                className="absolute top-full left-0 pt-2"
                onMouseEnter={handleSubmenuEnter}
                onMouseLeave={handleDropdownLeave}
              >
                <div className="bg-[#1C1917] border border-[#D4AF37]/20 rounded-lg shadow-xl min-w-[200px]">
                  <a href="https://gillesdelieuze.com/axiom-cursus-immersion/" className="block px-5 py-3 text-[#A8A29E] text-sm hover:text-[#FAFAF9] hover:bg-[#D4AF37]/10 transition-colors">
                    AXIOM
                  </a>
                  <a href="https://gillesdelieuze.com/processus-morpheus-pdv/" className="block px-5 py-3 text-[#A8A29E] text-sm hover:text-[#FAFAF9] hover:bg-[#D4AF37]/10 transition-colors">
                    Processus Morpheus
                  </a>
                  <a href="https://gillesdelieuze.com/accompagnements-11/" className="block px-5 py-3 text-[#A8A29E] text-sm hover:text-[#FAFAF9] hover:bg-[#D4AF37]/10 transition-colors">
                    One To One
                  </a>
                </div>
              </div>
            )}
          </div>

          {/* À PROPOS */}
          <div
            className="relative"
            onMouseEnter={() => handleDropdownEnter('apropos')}
            onMouseLeave={handleDropdownLeave}
          >
            <span className="flex items-center gap-1 text-[#FAFAF9] text-sm uppercase tracking-wider font-medium px-4 py-2 hover:text-[#D4AF37] transition-colors duration-300 cursor-pointer">
              À propos
              <ChevronDown size={14} className="opacity-60" />
            </span>
            
            {activeDropdown === 'apropos' && (
              <div 
                className="absolute top-full left-0 pt-2"
                onMouseEnter={handleSubmenuEnter}
                onMouseLeave={handleDropdownLeave}
              >
                <div className="bg-[#1C1917] border border-[#D4AF37]/20 rounded-lg shadow-xl min-w-[200px]">
                  <a href="https://gillesdelieuze.com/a-propos/" className="block px-5 py-3 text-[#A8A29E] text-sm hover:text-[#FAFAF9] hover:bg-[#D4AF37]/10 transition-colors">
                    Qui est Gilles ?
                  </a>
                  <a href="https://gillesdelieuze.com/temoignages/" className="block px-5 py-3 text-[#A8A29E] text-sm hover:text-[#FAFAF9] hover:bg-[#D4AF37]/10 transition-colors">
                    Témoignages
                  </a>
                  <a href="https://gillesdelieuze.com/contact/" className="block px-5 py-3 text-[#A8A29E] text-sm hover:text-[#FAFAF9] hover:bg-[#D4AF37]/10 transition-colors">
                    Contact
                  </a>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Right Side Actions */}
        <div className="hidden lg:flex items-center gap-3">
          <a
            href="https://gillesdelieuze.com/?s="
            className="text-[#FAFAF9] hover:text-[#D4AF37] transition-colors p-2"
            data-testid="nav-search"
            title="Rechercher"
          >
            <Search size={20} strokeWidth={1.5} />
          </a>

          <a
            href="https://gillesdelieuze.com/cart/"
            className="text-[#FAFAF9] hover:text-[#D4AF37] transition-colors p-2"
            data-testid="nav-cart"
            title="Panier"
          >
            <ShoppingCart size={20} strokeWidth={1.5} />
          </a>

          <a
            href="https://gillesdelieuze.com/wp-login.php"
            data-testid="nav-login"
            className="flex items-center gap-2 border border-[#D4AF37]/50 text-[#D4AF37] rounded-full px-5 py-2 text-sm uppercase tracking-wider font-medium hover:bg-[#D4AF37] hover:text-white transition-all duration-300"
          >
            <User size={16} strokeWidth={1.5} />
            Se connecter
          </a>
        </div>

        {/* Mobile Menu Button */}
        <div className="flex lg:hidden items-center gap-3">
          <a
            href="https://gillesdelieuze.com/?s="
            className="text-[#FAFAF9] hover:text-[#D4AF37] transition-colors p-2"
          >
            <Search size={20} strokeWidth={1.5} />
          </a>
          <button
            className="text-[#FAFAF9] p-2"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            data-testid="mobile-menu-btn"
          >
            {isMobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-[#1C1917] border-t border-[#D4AF37]/20 max-h-[80vh] overflow-y-auto"
          >
            <div className="px-4 py-6 space-y-1">
              {/* Formations Mobile */}
              <div className="border-b border-[#D4AF37]/10">
                <p className="text-[#FAFAF9] text-base uppercase tracking-wider font-medium py-3">
                  Formations
                </p>
                <div className="pl-4 pb-3 space-y-2">
                  <a href="https://gillesdelieuze.com/lecole-des-maitres-dauphins/" className="block text-[#A8A29E] text-sm py-1 hover:text-[#D4AF37]">
                    École Dauphins
                  </a>
                  <a href="https://gillesdelieuze.com/accordeur-quantique-2024/" className="block text-[#A8A29E] text-sm py-1 hover:text-[#D4AF37]">
                    Accordeur Quantique
                  </a>
                  <p className="text-[#D4AF37] text-xs uppercase tracking-wider pt-2">Toutes les formations</p>
                  <a href="https://gillesdelieuze.com/quantic-revolution/" className="block text-[#A8A29E] text-sm py-1 pl-3 hover:text-[#D4AF37]">
                    Quantic Revolution
                  </a>
                  <a href="https://gillesdelieuze.com/galactique-pdv/" className="block text-[#A8A29E] text-sm py-1 pl-3 hover:text-[#D4AF37]">
                    Initiation Langage Galactique
                  </a>
                  <a href="https://gillesdelieuze.com/processus-morpheus/" className="block text-[#A8A29E] text-sm py-1 pl-3 hover:text-[#D4AF37]">
                    Morpheus
                  </a>
                  <p className="text-[#D4AF37] text-xs uppercase tracking-wider pt-2">Programmes</p>
                  <a href="https://gillesdelieuze.com/quantum-love/" className="block text-[#A8A29E] text-sm py-1 pl-3 hover:text-[#D4AF37]">
                    Quantum Love
                  </a>
                  <a href="https://gillesdelieuze.com/intuitive-clarity/" className="block text-[#A8A29E] text-sm py-1 pl-3 hover:text-[#D4AF37]">
                    Intuitive Clarity
                  </a>
                  <a href="https://gillesdelieuze.com/dolphin-bliss/" className="block text-[#A8A29E] text-sm py-1 pl-3 hover:text-[#D4AF37]">
                    Dolphin Bliss
                  </a>
                  <a href="https://gillesdelieuze.com/infinity-serenity/" className="block text-[#A8A29E] text-sm py-1 pl-3 hover:text-[#D4AF37]">
                    Infinity Serenity
                  </a>
                  <a href="https://gillesdelieuze.com/quantum-harmonics/" className="block text-[#A8A29E] text-sm py-1 pl-3 hover:text-[#D4AF37]">
                    Quantum Harmonics
                  </a>
                  <a href="https://gillesdelieuze.com/dolphin-bootcamp/" className="block text-[#A8A29E] text-sm py-1 hover:text-[#D4AF37]">
                    Dolphin Bootcamp
                  </a>
                  <a href="https://gillesdelieuze.com/programme-joie-dauphin/" className="block text-[#A8A29E] text-sm py-1 hover:text-[#D4AF37]">
                    Renaître à la Joie
                  </a>
                </div>
              </div>

              {/* Voyages Mobile */}
              <div className="border-b border-[#D4AF37]/10">
                <p className="text-[#FAFAF9] text-base uppercase tracking-wider font-medium py-3">
                  Voyages
                </p>
                <div className="pl-4 pb-3 space-y-2">
                  <a href="https://gillesdelieuze.com/voyages-gd/" className="block text-[#A8A29E] text-sm py-1 hover:text-[#D4AF37]">
                    Tous les voyages
                  </a>
                  <a href="https://gillesdelieuze.com/voyage-dauphin/" className="block text-[#A8A29E] text-sm py-1 hover:text-[#D4AF37]">
                    Dauphins Mer Rouge
                  </a>
                  <a href="https://gillesdelieuze.com/madagascar/" className="block text-[#A8A29E] text-sm py-1 hover:text-[#D4AF37]">
                    Madagascar
                  </a>
                </div>
              </div>

              {/* Mentorat Mobile */}
              <div className="border-b border-[#D4AF37]/10">
                <p className="text-[#FAFAF9] text-base uppercase tracking-wider font-medium py-3">
                  Mentorat
                </p>
                <div className="pl-4 pb-3 space-y-2">
                  <a href="https://gillesdelieuze.com/axiom-cursus-immersion/" className="block text-[#A8A29E] text-sm py-1 hover:text-[#D4AF37]">
                    AXIOM
                  </a>
                  <a href="https://gillesdelieuze.com/processus-morpheus-pdv/" className="block text-[#A8A29E] text-sm py-1 hover:text-[#D4AF37]">
                    Processus Morpheus
                  </a>
                  <a href="https://gillesdelieuze.com/accompagnements-11/" className="block text-[#A8A29E] text-sm py-1 hover:text-[#D4AF37]">
                    One To One
                  </a>
                </div>
              </div>

              {/* À propos Mobile */}
              <div className="border-b border-[#D4AF37]/10">
                <p className="text-[#FAFAF9] text-base uppercase tracking-wider font-medium py-3">
                  À propos
                </p>
                <div className="pl-4 pb-3 space-y-2">
                  <a href="https://gillesdelieuze.com/a-propos/" className="block text-[#A8A29E] text-sm py-1 hover:text-[#D4AF37]">
                    Qui est Gilles ?
                  </a>
                  <a href="https://gillesdelieuze.com/temoignages/" className="block text-[#A8A29E] text-sm py-1 hover:text-[#D4AF37]">
                    Témoignages
                  </a>
                  <a href="https://gillesdelieuze.com/contact/" className="block text-[#A8A29E] text-sm py-1 hover:text-[#D4AF37]">
                    Contact
                  </a>
                </div>
              </div>
              
              {/* Mobile Actions */}
              <div className="pt-4 space-y-3">
                <a
                  href="https://gillesdelieuze.com/cart/"
                  className="flex items-center gap-2 text-[#FAFAF9] py-2"
                >
                  <ShoppingCart size={18} />
                  Mon panier
                </a>
                <a
                  href="https://gillesdelieuze.com/wp-login.php"
                  className="block text-center bg-gradient-to-r from-[#D4AF37] to-[#AA8C2C] text-white rounded-full px-6 py-3 text-sm uppercase tracking-widest font-bold"
                >
                  Se connecter
                </a>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navbar;
